export const CORPORATE_COMPANY = {
  shortName: "魔法向量",
  fullName: "上海魔法向量人工智能技术有限公司",
  tagline: "AI 赋能组织 · 知识 · 创作",
  slogan: "用 AI 重塑组织生产力，让知识流动起来",
  intro:
    "魔法向量聚焦 AI 与组织管理、知识分享、影视创作三大方向，为企业提供 AI 产研组织培训与赋能、大V数字人知识平台，以及 AI 影视内容生产能力。从理论宣讲、实践陪伴到系统落地，帮助团队真正用好 AI。",
  email: "contact@magicvector.cn",
  founded: "2026",
  employees: "30+",
  clients: "20+",
  projects: "50+",
};

export type CorporateNavItem = {
  key: string;
  label: string;
  path: string;
};

export const CORPORATE_NAV: CorporateNavItem[] = [
  { key: "home", label: "首页", path: "/" },
  { key: "about", label: "关于我们", path: "/about" },
  { key: "solutions", label: "业务方向", path: "/solutions" },
  { key: "cases", label: "客户案例", path: "/cases" },
  { key: "news", label: "业务介绍", path: "/news" },
  { key: "contact", label: "联系我们", path: "/contact" },
];

export type CorporateSolutionModule = {
  name: string;
  desc: string;
  points: string[];
};

export type CorporateSolution = {
  id: string;
  title: string;
  summary: string;
  highlights: string[];
  tags: string[];
  status?: "active" | "research";
  modules?: CorporateSolutionModule[];
};

export const CORPORATE_SOLUTIONS: CorporateSolution[] = [
  {
    id: "ai-org",
    title: "AI + 组织管理",
    summary: "AI 产研组织培训与赋能，帮助团队建立 AI 编程体系、可视化协作机制与结果导向的管理文化。",
    highlights: ["理论宣讲课", "1 个月实践陪伴", "AI 产研管理系统"],
    tags: ["主力方向", "ToB"],
    status: "active",
    modules: [
      {
        name: "理论 · 宣讲课",
        desc: "建立团队对 AI 编程与组织协作的正确认知。",
        points: [
          "AI 编程解决了什么：编码效率大幅提升，架构设计与代码审查仍需人来做",
          "日交付可视化晨会与周交付管理：面向协作、面向过程、面向集体",
          "打造面向结果而非面向时间的组织",
        ],
      },
      {
        name: "实践 · 1 个月陪伴套餐",
        desc: "打木人巷式陪跑，能演示的尽量不说，拒绝空洞日报。",
        points: [
          "编写安装环境搭建教程：基于 MagicVector 基座，快速搭建一套新系统",
          "每日可视化晨会教练：演示驱动，拒绝形式化日报",
          "日常 AI 教练：及时纠正错误用法，解决员工实际问题",
        ],
      },
      {
        name: "系统 · AI 产研管理系统",
        desc: "将方法论沉淀为可运行的管理工具。",
        points: [
          "麻雀项目管理：轻量敏捷，持续迭代完善中",
          "绩效考核系统：与客户共创，沉淀 AI 时代最佳实践",
        ],
      },
    ],
  },
  {
    id: "ai-knowledge",
    title: "AI + 知识分享",
    summary: "大V 数字人平台，类似 AI 版知识星球——大V 入驻引流，平台提供人 + AI 的复合服务。",
    highlights: ["AI 维护粉丝关系", "AI 输出大V观点", "AI 学习资料与考试"],
    tags: ["主力方向", "ToC / ToB"],
    status: "active",
    modules: [
      {
        name: "大V 数字人平台",
        desc: "让大V 的影响力与 AI 的服务能力结合，放大知识传播效率。",
        points: [
          "AI 代替大V 维护粉丝关系，7×24 响应互动",
          "AI 代替大V 给出观点：支持大V 预设知识库，保持风格一致",
          "AI 学习资料准备与考试：帮助粉丝系统化吸收大V 的知识体系",
        ],
      },
    ],
  },
  {
    id: "ai-media",
    title: "AI + 影视制作",
    summary: "探索 AI 驱动的内容生产方式，覆盖短剧在线生产与高质量 3D 片段制作。",
    highlights: ["AI 短剧在线生产", "AI 高质量 3D 片段", "按 Token 付费"],
    tags: ["调研中"],
    status: "research",
    modules: [
      {
        name: "AI 短剧在线生产",
        desc: "从脚本到视频的全链路 AI 生产。",
        points: ["AI 制作脚本", "AI 生成视频", "在线协作生产流程"],
      },
      {
        name: "AI 高质量 3D 片段制作",
        desc: "承接电影公司高品质片段需求。",
        points: ["电影级 3D 片段生成", "按需定制场景与角色", "按 Token 用量计费"],
      },
    ],
  },
];

export type CorporateCase = {
  id: string;
  client: string;
  industry: string;
  title: string;
  result: string;
  detail: string;
};

export const CORPORATE_CASES: CorporateCase[] = [
  {
    id: "case-1",
    client: "某互联网科技公司",
    industry: "AI + 组织管理",
    title: "AI 编程体系搭建与 1 个月陪伴落地",
    result: "团队 AI 编码覆盖率达 100%，日交付可视化晨会全面落地",
    detail:
      "基于 MagicVector 基座完成开发环境标准化，通过每日可视化晨会教练与 AI 教练陪跑，团队在 1 个月内建立 AI 编程工作流，拒绝形式化日报，转向面向结果的协作模式。",
  },
  {
    id: "case-2",
    client: "某金融科技企业",
    industry: "AI + 组织管理",
    title: "AI 产研管理系统试点",
    result: "项目管理可视化，周交付节奏稳定，绩效评估机制初步成型",
    detail:
      "部署麻雀项目管理系统，配合周交付管理机制，帮助产研团队从面向时间转向面向结果，绩效考核模块与客户共创迭代中。",
  },
  {
    id: "case-3",
    client: "某行业意见领袖",
    industry: "AI + 知识分享",
    title: "大V 数字人平台内测试点",
    result: "粉丝互动响应效率提升 5 倍，知识库驱动的 AI 观点输出获粉丝好评",
    detail:
      "大V 预设个人知识库与观点风格，AI 代替大V 维护粉丝关系并输出一致风格的内容，同时提供 AI 学习资料与考试功能，帮助粉丝系统化学习。",
  },
  {
    id: "case-4",
    client: "某内容创作团队",
    industry: "AI + 影视制作",
    title: "AI 短剧生产流程调研",
    result: "完成从脚本到视频的全链路可行性验证",
    detail:
      "与内容创作团队联合调研 AI 短剧在线生产流程，验证 AI 脚本生成与视频合成的技术路径，为后续按 Token 付费的 SaaS 模式奠定基础。",
  },
];

export type CorporateNews = {
  id: string;
  title: string;
  date: string;
  category: string;
  summary: string;
  content: string;
};

export const CORPORATE_NEWS: CorporateNews[] = [
  {
    id: "news-1",
    title: "AI + 组织管理：我们在做什么",
    date: "2026",
    category: "介绍",
    summary: "面向企业的 AI 产研组织培训与赋能，涵盖理论宣讲、实践陪伴与管理系统建设。",
    content:
      "这是我们当前的主攻方向之一。我们希望帮助团队理解：AI 编程能大幅提升编码效率，但架构设计与代码审查仍需要人来做；组织上则需要日交付可视化晨会、周交付管理，以及面向结果而非面向时间的协作方式。服务设想分为三层——理论宣讲课、1 个月实践陪伴（环境搭建、晨会教练、日常 AI 教练）、以及麻雀项目管理和绩效考核相关的管理系统。以上均为我们在探索与建设中的方向，欢迎交流探讨。",
  },
  {
    id: "news-2",
    title: "AI + 知识分享：大V 数字人平台",
    date: "2026",
    category: "介绍",
    summary: "设想中的 AI 版知识社区——大V 入驻引流，平台提供人 + AI 的复合服务。",
    content:
      "这是我们规划中的另一个方向。平台设想是让大V 通过自身影响力引入粉丝，由 AI 辅助完成日常互动与知识传播：AI 帮助维护粉丝关系、基于大V 预设知识库输出一致风格的观点，并提供学习资料与考试等辅助功能。目前仍处于方案梳理与能力积累阶段，具体形态将持续迭代，欢迎有意向的大V 或机构与我们沟通。",
  },
  {
    id: "news-3",
    title: "AI + 影视制作：我们在关注什么",
    date: "2026",
    category: "介绍",
    summary: "关注 AI 短剧在线生产与高质量 3D 片段制作，盈利模式设想为按 Token 付费。",
    content:
      "影视制作是我们正在调研中的方向，尚未形成成熟产品。我们关注两条路径：一是 AI 短剧在线生产，覆盖脚本撰写到视频生成；二是承接电影公司需求的高质量 3D 片段制作。商业模式上倾向于按 Token 用量计费。该方向仍在前沿探索阶段，欢迎有相关场景或需求的伙伴一起探讨可行性。",
  },
  {
    id: "news-4",
    title: "关于魔法向量：三大业务方向",
    date: "2026",
    category: "介绍",
    summary: "公司聚焦 AI + 组织管理、AI + 知识分享、AI + 影视制作三个方向。",
    content:
      "上海魔法向量人工智能技术有限公司成立于 2026 年，当前战略聚焦三大方向：AI 产研组织培训与赋能（主力）、大V 数字人知识平台（主力）、AI 影视内容生产（调研中）。我们希望通过官网向访客介绍我们在做什么、关注什么，而非宣称已有成熟产品上市。如有合作或交流意向，欢迎通过联系我们页面留言。",
  },
];

export const CORPORATE_STATS = [
  { label: "成立年份", value: CORPORATE_COMPANY.founded },
  { label: "业务方向", value: "3" },
  { label: "服务客户", value: CORPORATE_COMPANY.clients },
  { label: "落地项目", value: CORPORATE_COMPANY.projects },
];

export const CORPORATE_VALUES = [
  {
    title: "能演示不说",
    desc: "拒绝空洞汇报与形式化日报，用可视化晨会和实际交付说话。",
  },
  {
    title: "面向结果",
    desc: "打造面向结果而非面向时间的组织，以业务产出为唯一验收标准。",
  },
  {
    title: "陪伴式落地",
    desc: "1 个月打木人巷式陪跑，从环境搭建到日常 AI 教练，确保团队真正会用。",
  },
  {
    title: "系统沉淀",
    desc: "将方法论沉淀为麻雀项目管理、绩效考核等可运行的系统工具。",
  },
];

export const CORPORATE_TIMELINE = [
  { year: "2026", event: "公司在上海成立，明确 AI + 组织管理、知识分享、影视制作三大方向。" },
  { year: "2026", event: "梳理 AI 产研组织赋能的服务框架，探索大V 数字人平台方案。" },
  { year: "进行中", event: "持续打磨麻雀项目管理等工具能力，调研 AI 影视制作可行性。" },
];

export const CORPORATE_DELIVERY_STEPS = [
  {
    title: "1. 理论宣讲",
    desc: "通过宣讲课建立团队对 AI 编程、可视化协作与结果导向管理的正确认知。",
  },
  {
    title: "2. 实践陪伴",
    desc: "1 个月打木人巷式陪跑：环境搭建、每日晨会教练、日常 AI 教练，能演示的不说。",
  },
  {
    title: "3. 系统落地",
    desc: "部署 AI 产研管理系统，麻雀项目管理 + 绩效考核共创，将方法论固化为工具。",
  },
];
